/**
 * @fileoverview Business Logic (BL) for Adivina Adivinador (Riddle - Tile 10).
 * Implements input validation, SQL injection security checks, data normalization,
 * and orchestration with the Data Layer (DL).
 * 
 * @module utilities/BL/Admin/Games/Riddle
 */

import * as DLRiddle from './../../../DL/Admin/Games/Riddle';
import { ValidateSqlInjection } from './../../Security';

// =============================================================================
// TRIVIA SETS BUSINESS LOGIC
// =============================================================================

/**
 * Retrieves all registered trivia sets.
 * 
 * @async
 * @function getTriviaSets
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const getTriviaSets = async () => {
    try {
        return await DLRiddle.getTriviaSets();
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al obtener los sets de trivias de Adivina Adivinador.'
        };
    }
};

/**
 * Retrieves a single trivia set by GUID with validation.
 * 
 * @async
 * @function getTriviaSetById
 * @param {string} triviaSetId - Unique GUID of the trivia set.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getTriviaSetById = async (triviaSetId) => {
    try {
        if (!triviaSetId || typeof triviaSetId !== 'string' || triviaSetId.trim() === '') {
            return { success: false, message: 'El identificador de la trivia es requerido.' };
        }

        return await DLRiddle.getTriviaSetById(triviaSetId.trim());
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al obtener el detalle de la trivia.'
        };
    }
};

/**
 * Validates and saves a trivia set.
 * 
 * @async
 * @function saveTriviaSet
 * @param {Object} triviaSetData - Trivia set payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const saveTriviaSet = async (triviaSetData) => {
    try {
        if (!triviaSetData || typeof triviaSetData !== 'object') {
            return { success: false, message: 'Los datos del paquete de trivia son requeridos.' };
        }

        // Validate Title
        if (!triviaSetData.title || typeof triviaSetData.title !== 'string' || triviaSetData.title.trim() === '') {
            return { success: false, message: 'El título del paquete de trivia es obligatorio.' };
        }

        const trimmedTitle = triviaSetData.title.trim();
        if (trimmedTitle.length > 200) {
            return { success: false, message: 'El título no puede exceder 200 caracteres.' };
        }

        const isTitleThreat = await ValidateSqlInjection(trimmedTitle);
        if (isTitleThreat) {
            return { success: false, message: 'Se detectaron caracteres o patrones no permitidos en el título.' };
        }

        // Validate Description if provided
        let trimmedDescription = null;
        if (triviaSetData.description && typeof triviaSetData.description === 'string') {
            trimmedDescription = triviaSetData.description.trim();
            if (trimmedDescription.length > 1000) {
                return { success: false, message: 'La descripción no puede exceder 1000 caracteres.' };
            }
            const isDescThreat = await ValidateSqlInjection(trimmedDescription);
            if (isDescThreat) {
                return { success: false, message: 'Se detectaron caracteres o patrones no permitidos en la descripción.' };
            }
        }

        const payload = {
            id: triviaSetData.id || undefined,
            title: trimmedTitle,
            description: trimmedDescription,
            isActive: triviaSetData.isActive !== false,
            questions: Array.isArray(triviaSetData.questions) ? triviaSetData.questions : undefined
        };

        return await DLRiddle.saveTriviaSet(payload);
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al validar y guardar el set de trivia.'
        };
    }
};

/**
 * Deletes a trivia set after ID validation.
 * 
 * @async
 * @function deleteTriviaSet
 * @param {string} triviaSetId - Unique GUID.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const deleteTriviaSet = async (triviaSetId) => {
    try {
        if (!triviaSetId || typeof triviaSetId !== 'string' || triviaSetId.trim() === '') {
            return { success: false, message: 'El identificador de la trivia a eliminar es requerido.' };
        }

        return await DLRiddle.deleteTriviaSet(triviaSetId.trim());
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al eliminar el set de trivia.'
        };
    }
};

// =============================================================================
// QUESTIONS BUSINESS LOGIC
// =============================================================================

/**
 * Validates and saves an individual question with its options.
 * 
 * @async
 * @function saveQuestion
 * @param {Object} questionData - Question payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const saveQuestion = async (questionData) => {
    try {
        if (!questionData || typeof questionData !== 'object') {
            return { success: false, message: 'La información de la pregunta es obligatoria.' };
        }

        // Validate Instruction
        if (!questionData.instruction || typeof questionData.instruction !== 'string' || questionData.instruction.trim() === '') {
            return { success: false, message: 'La frase o instrucción de la adivinanza es obligatoria.' };
        }

        const trimmedInstruction = questionData.instruction.trim();
        if (trimmedInstruction.length > 500) {
            return { success: false, message: 'La instrucción no puede exceder 500 caracteres.' };
        }

        const isInstructionThreat = await ValidateSqlInjection(trimmedInstruction);
        if (isInstructionThreat) {
            return { success: false, message: 'Se detectó un patrón inseguro en la frase de la pregunta.' };
        }

        // Validate ImageUrl (Silhouette)
        if (!questionData.imageUrl || typeof questionData.imageUrl !== 'string' || questionData.imageUrl.trim() === '') {
            return { success: false, message: 'La imagen o silueta principal es obligatoria.' };
        }

        // Validate Response Text / Image
        let trimmedResponseText = null;
        if (questionData.responseText && typeof questionData.responseText === 'string') {
            trimmedResponseText = questionData.responseText.trim();
            if (trimmedResponseText.length > 500) {
                return { success: false, message: 'El texto de respuesta no puede exceder 500 caracteres.' };
            }
            const isRespThreat = await ValidateSqlInjection(trimmedResponseText);
            if (isRespThreat) {
                return { success: false, message: 'Se detectó un patrón inseguro en el texto de respuesta.' };
            }
        }

        // Validate Options
        if (!Array.isArray(questionData.options) || questionData.options.length < 2 || questionData.options.length > 8) {
            return { success: false, message: 'La adivinanza debe contar con al menos 2 opciones de respuesta (máximo 8).' };
        }

        let hasCorrectOption = false;
        const sanitizedOptions = [];

        for (let i = 0; i < questionData.options.length; i++) {
            const opt = questionData.options[i];
            if (!opt.text || typeof opt.text !== 'string' || opt.text.trim() === '') {
                return { success: false, message: `La opción ${i + 1} no puede estar vacía.` };
            }

            const trimmedOptionText = opt.text.trim();
            if (trimmedOptionText.length > 300) {
                return { success: false, message: `El texto de la opción ${i + 1} excede 300 caracteres.` };
            }

            const isOptThreat = await ValidateSqlInjection(trimmedOptionText);
            if (isOptThreat) {
                return { success: false, message: `Se detectó un patrón inseguro en la opción ${i + 1}.` };
            }

            if (opt.isCorrect === true || opt.isCorrect === 'true' || opt.isCorrect === 1) {
                hasCorrectOption = true;
            }

            sanitizedOptions.push({
                id: opt.id || undefined,
                questionId: opt.questionId || questionData.id || undefined,
                text: trimmedOptionText,
                imageUrl: opt.imageUrl ? opt.imageUrl.trim() : null,
                isCorrect: Boolean(opt.isCorrect),
                orderIndex: opt.orderIndex !== undefined ? Number(opt.orderIndex) : i
            });
        }

        if (!hasCorrectOption) {
            return { success: false, message: 'Debes marcar al menos una opción como la respuesta correcta.' };
        }

        const payload = {
            id: questionData.id || undefined,
            triviaSetId: questionData.triviaSetId || undefined,
            instruction: trimmedInstruction,
            imageUrl: questionData.imageUrl.trim(),
            responseImageUrl: questionData.responseImageUrl ? questionData.responseImageUrl.trim() : null,
            responseText: trimmedResponseText,
            orderIndex: questionData.orderIndex !== undefined ? Number(questionData.orderIndex) : 0,
            isActive: questionData.isActive !== false,
            options: sanitizedOptions
        };

        return await DLRiddle.saveQuestion(payload);
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al validar y guardar la pregunta.'
        };
    }
};

/**
 * Deletes an individual question.
 * 
 * @async
 * @function deleteQuestion
 * @param {string} questionId - Unique GUID.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const deleteQuestion = async (questionId) => {
    try {
        if (!questionId || typeof questionId !== 'string' || questionId.trim() === '') {
            return { success: false, message: 'El identificador de la pregunta a eliminar es requerido.' };
        }

        return await DLRiddle.deleteQuestion(questionId.trim());
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al eliminar la pregunta.'
        };
    }
};

// =============================================================================
// SETTINGS & THEMES BUSINESS LOGIC
// =============================================================================

/**
 * Retrieves settings for Riddle.
 * 
 * @async
 * @function getSettings
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getSettings = async () => {
    try {
        return await DLRiddle.getSettings();
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al obtener la configuración de Riddle.'
        };
    }
};

/**
 * Validates and updates settings for Riddle.
 * 
 * @async
 * @function updateSettings
 * @param {Object} settingsData - Settings payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const updateSettings = async (settingsData) => {
    try {
        if (!settingsData || typeof settingsData !== 'object') {
            return { success: false, message: 'La información de configuración es obligatoria.' };
        }

        // Validate Cover Title
        if (!settingsData.coverTitle || typeof settingsData.coverTitle !== 'string' || settingsData.coverTitle.trim() === '') {
            return { success: false, message: 'El título de portada es obligatorio.' };
        }
        const trimmedCoverTitle = settingsData.coverTitle.trim();
        if (trimmedCoverTitle.length > 200) {
            return { success: false, message: 'El título de portada no puede exceder 200 caracteres.' };
        }
        if (await ValidateSqlInjection(trimmedCoverTitle)) {
            return { success: false, message: 'Se detectó un patrón inseguro en el título de portada.' };
        }

        // Validate Cover Subtitle
        if (!settingsData.coverSubtitle || typeof settingsData.coverSubtitle !== 'string' || settingsData.coverSubtitle.trim() === '') {
            return { success: false, message: 'El subtítulo de portada es obligatorio.' };
        }
        const trimmedCoverSubtitle = settingsData.coverSubtitle.trim();
        if (trimmedCoverSubtitle.length > 300) {
            return { success: false, message: 'El subtítulo de portada no puede exceder 300 caracteres.' };
        }
        if (await ValidateSqlInjection(trimmedCoverSubtitle)) {
            return { success: false, message: 'Se detectó un patrón inseguro en el subtítulo de portada.' };
        }

        // Validate Header Title
        const headerTitle = settingsData.headerTitle ? settingsData.headerTitle.trim() : 'ADIVINA ADIVINADOR';
        if (await ValidateSqlInjection(headerTitle)) {
            return { success: false, message: 'Se detectó un patrón inseguro en el título de cabecera.' };
        }

        // Validate Layout Mode (1 to 4)
        const screenLayout = Number(settingsData.screenLayout) || 1;
        if (screenLayout < 1 || screenLayout > 4) {
            return { success: false, message: 'El layout de pantalla seleccionado es inválido (debe ser del 1 al 4).' };
        }

        const payload = {
            activeTriviaSetId: settingsData.activeTriviaSetId || null,
            screenLayout: screenLayout,
            showCorrectAnswer: settingsData.showCorrectAnswer !== false,
            coverTitle: trimmedCoverTitle,
            coverSubtitle: trimmedCoverSubtitle,
            coverBackgroundType: settingsData.coverBackgroundType || 'color',
            coverBackgroundColor: settingsData.coverBackgroundColor || '#0A1128',
            coverBackgroundImageUrl: settingsData.coverBackgroundImageUrl || null,
            coverTextColor: settingsData.coverTextColor || '#FFFFFF',
            headerTitle: headerTitle,
            brandLogoUrl: settingsData.brandLogoUrl || null,
            sponsorLogoUrl: settingsData.sponsorLogoUrl || null,
            gameBackgroundType: settingsData.gameBackgroundType || 'color',
            gameBackgroundColor: settingsData.gameBackgroundColor || '#001F54',
            gameBackgroundImageUrl: settingsData.gameBackgroundImageUrl || null,
            gameTextColor: settingsData.gameTextColor || '#FFFFFF',
            fontFamily: settingsData.fontFamily || 'Arial'
        };

        return await DLRiddle.updateSettings(payload);
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al validar y actualizar la configuración.'
        };
    }
};

/**
 * Retrieves active trivia set.
 * 
 * @async
 * @function getActiveTriviaSet
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getActiveTriviaSet = async () => {
    try {
        return await DLRiddle.getActiveTriviaSet();
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al obtener la trivia activa.'
        };
    }
};

// =============================================================================
// MEDIA UPLOAD BUSINESS LOGIC
// =============================================================================

/**
 * Validates and uploads media file.
 * 
 * @async
 * @function uploadMedia
 * @param {File|Blob} file - Binary file.
 * @param {string} [assetType='general'] - Type identifier.
 * @param {string|null} [triviaSetId=null] - Optional associated trivia set GUID.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>}
 */
export const uploadMedia = async (file, assetType = 'general', triviaSetId = null) => {
    try {
        if (!file) {
            return { success: false, message: 'Debe seleccionar un archivo para subir.' };
        }

        // Limit file size to 25MB
        const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return { success: false, message: 'El archivo excede el límite máximo permitido de 25 MB.' };
        }

        return await DLRiddle.uploadMedia(file, assetType, triviaSetId);
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al procesar la subida del archivo multimedia.'
        };
    }
};

// =============================================================================
// REAL-TIME ACTIONS
// =============================================================================

/**
 * Projects the Cover view to /screen.
 * 
 * @async
 * @function showCover
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const showCover = async () => {
    try {
        return await DLRiddle.showCover();
    } catch (error) {
        return { success: false, message: error.message || 'Error al proyectar la portada.' };
    }
};

/**
 * Projects the Game arena view to /screen.
 * 
 * @async
 * @function showGame
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const showGame = async () => {
    try {
        return await DLRiddle.showGame();
    } catch (error) {
        return { success: false, message: error.message || 'Error al proyectar el escenario de juego.' };
    }
};

/**
 * Sends an active question to Screen and mobile fans with options, layout, and reveal settings.
 * 
 * @async
 * @function sendQuestion
 * @param {Object} question - Question object.
 * @param {number} [screenLayout=1] - Layout mode.
 * @param {boolean} [showCorrectAnswer=true] - Whether to show correct answer on reveal.
 * @param {number|null} [questionNumber=null] - 1-based question sequence index.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const sendQuestion = async (question, screenLayout = 1, showCorrectAnswer = true, questionNumber = null) => {
    try {
        if (!question) {
            return { success: false, message: 'No hay ninguna pregunta seleccionada para enviar.' };
        }

        // Map question to the DTO expected by backend SendRiddleQuestionRealtimeRequestDto
        const formattedOptions = (question.options || []).map((opt, idx) => ({
            id: typeof opt.id === 'number' ? opt.id : idx + 1,
            text: opt.text || '',
            image: opt.imageUrl || opt.image || '',
            isCorrect: Boolean(opt.isCorrect)
        }));

        const resolvedQuestionId = Number(questionNumber) > 0 
            ? Number(questionNumber) 
            : (typeof question.id === 'number' ? question.id : (question.orderIndex !== undefined ? question.orderIndex + 1 : 1));

        const payload = {
            id: resolvedQuestionId,
            instruction: question.instruction || 'Adivina qué personaje o elemento es',
            image: question.imageUrl || question.image || '',
            options: formattedOptions,
            response: {
                text: question.responseText || question.response?.text || '',
                image: question.responseImageUrl || question.response?.image || ''
            },
            screenLayout: Number(screenLayout) || 1,
            showCorrectAnswer: Boolean(showCorrectAnswer)
        };

        return await DLRiddle.sendQuestion(payload);
    } catch (error) {
        return { success: false, message: error.message || 'Error al emitir la adivinanza a los usuarios.' };
    }
};

/**
 * Displays/reveals hidden item on screen and mobile.
 * 
 * @async
 * @function displayItem
 * @param {Object|null} [question=null] - Current question.
 * @param {number|null} [questionNumber=null] - 1-based question sequence index.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const displayItem = async (question = null, questionNumber = null) => {
    try {
        let questionDto = null;
        if (question) {
            const resolvedQuestionId = Number(questionNumber) > 0 
                ? Number(questionNumber) 
                : (typeof question.id === 'number' ? question.id : 1);

            questionDto = {
                id: resolvedQuestionId,
                instruction: question.instruction || '',
                image: question.imageUrl || question.image || '',
                options: (question.options || []).map((opt, idx) => ({
                    id: typeof opt.id === 'number' ? opt.id : idx + 1,
                    text: opt.text || '',
                    image: opt.imageUrl || opt.image || '',
                    isCorrect: Boolean(opt.isCorrect)
                })),
                response: {
                    text: question.responseText || question.response?.text || '',
                    image: question.responseImageUrl || question.response?.image || ''
                }
            };
        }

        return await DLRiddle.displayItem(questionDto);
    } catch (error) {
        return { success: false, message: error.message || 'Error al revelar el elemento en pantalla.' };
    }
};

/**
 * Changes active layout mode in real time.
 * 
 * @async
 * @function changeLayout
 * @param {number} screenLayout - Layout mode (1..4).
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const changeLayout = async (screenLayout) => {
    try {
        const parsedLayout = Number(screenLayout);
        if (isNaN(parsedLayout) || parsedLayout < 1 || parsedLayout > 4) {
            return { success: false, message: 'El modo de layout seleccionado no es válido.' };
        }

        return await DLRiddle.changeLayout(parsedLayout);
    } catch (error) {
        return { success: false, message: error.message || 'Error al cambiar la distribución de pantalla.' };
    }
};

/**
 * Emits final results and podium to screen.
 * 
 * @async
 * @function sendResults
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const sendResults = async () => {
    try {
        return await DLRiddle.sendResults();
    } catch (error) {
        return { success: false, message: error.message || 'Error al enviar resultados a la pantalla.' };
    }
};

/**
 * Resets in-memory results and counters.
 * 
 * @async
 * @function resetResults
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const resetResults = async () => {
    try {
        return await DLRiddle.resetResults();
    } catch (error) {
        return { success: false, message: error.message || 'Error al reiniciar los resultados.' };
    }
};

/**
 * Retrieves current real-time state snapshot.
 * 
 * @async
 * @function getRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getRealtimeState = async () => {
    try {
        return await DLRiddle.getRealtimeState();
    } catch (error) {
        return { success: false, message: error.message || 'Error al obtener el estado en tiempo real.' };
    }
};

/**
 * Retrieves accumulated results breakdown.
 * 
 * @async
 * @function getResults
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const getResults = async () => {
    try {
        return await DLRiddle.getResults();
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar resultados acumulados.' };
    }
};

// Aliases for PascalCase naming
export const GetTriviaSets = getTriviaSets;
export const GetTriviaSetById = getTriviaSetById;
export const SaveTriviaSet = saveTriviaSet;
export const DeleteTriviaSet = deleteTriviaSet;
export const SaveQuestion = saveQuestion;
export const DeleteQuestion = deleteQuestion;
export const GetSettings = getSettings;
export const UpdateSettings = updateSettings;
export const GetActiveTriviaSet = getActiveTriviaSet;
export const UploadMedia = uploadMedia;
export const ShowCover = showCover;
export const ShowGame = showGame;
export const SendQuestion = sendQuestion;
export const DisplayItem = displayItem;
export const ChangeLayout = changeLayout;
export const SendResults = sendResults;
export const ResetResults = resetResults;
export const GetRealtimeState = getRealtimeState;
export const GetResults = getResults;
